import { UserService } from './ApiService';
import LocalStorageService from './LocalStorageService';

/**
 * Service to sync plan versions and check-ins with the server
 * Automatically detects changes and updates local storage
 */
class PlanSyncService {
  /**
   * Sync plan versions and check-in from server
   * Updates local storage if changes are detected
   * @param {Object} options - Sync options
   * @param {boolean} options.forceSync - Force sync even if no changes detected
   * @returns {Object} Sync result with status and data
   */
  static async sync(options = {}) {
    const { forceSync = false } = options;
    
    try {
      console.log('Starting plan sync...');
      
      // Fetch latest data from server
      const serverData = await UserService.getPlanVersionsAndCheckIn();
      
      if (!serverData) {
        throw new Error('No data received from server');
      }

      // Check if plan has changed
      const planChangeCheck = await LocalStorageService.checkPlanChanged(serverData.planVersions);
      const checkInChangeCheck = await LocalStorageService.checkCheckInChanged(serverData.latestCheckIn);

      const hasChanges = planChangeCheck.hasChanged || checkInChangeCheck.hasChanged;

      // Update local storage if changes detected or force sync
      if (hasChanges || forceSync) {
        console.log('Changes detected or force sync requested. Updating local storage...');
        console.log('Plan changed:', planChangeCheck);
        console.log('Check-in changed:', checkInChangeCheck);

        await LocalStorageService.storePlanData(
          serverData.planVersions,
          serverData.latestCheckIn
        );

        return {
          success: true,
          synced: true,
          hasChanges,
          planChangeCheck,
          checkInChangeCheck,
          data: serverData,
          message: hasChanges ? 'Local storage updated with new data' : 'Force sync completed',
        };
      } else {
        console.log('No changes detected. Local storage is up to date.');
        return {
          success: true,
          synced: false,
          hasChanges: false,
          planChangeCheck,
          checkInChangeCheck,
          data: serverData,
          message: 'No changes detected',
        };
      }
    } catch (error) {
      console.error('Error during plan sync:', error);
      return {
        success: false,
        synced: false,
        error: error.message,
        message: 'Failed to sync plan data',
      };
    }
  }

  /**
   * Initialize sync - fetches and stores data if not already stored
   * Useful for first-time setup
   * @returns {Object} Initialization result
   */
  static async initialize() {
    try {
      console.log('Initializing plan sync...');
      
      const storedData = await LocalStorageService.getAllStoredData();
      
      // If no stored data, perform initial sync
      if (!storedData.planVersions || storedData.planVersions.length === 0) {
        console.log('No stored data found. Performing initial sync...');
        return await this.sync({ forceSync: true });
      }

      console.log('Stored data exists. Checking for updates...');
      return await this.sync({ forceSync: false });
    } catch (error) {
      console.error('Error initializing plan sync:', error);
      return {
        success: false,
        error: error.message,
        message: 'Failed to initialize plan sync',
      };
    }
  }

  /**
   * Get current data from local storage
   * @returns {Object} Stored data
   */
  static async getLocalData() {
    try {
      return await LocalStorageService.getAllStoredData();
    } catch (error) {
      console.error('Error getting local data:', error);
      return {
        planVersions: [],
        latestCheckIn: null,
        metadata: {},
      };
    }
  }

  /**
   * Get data formatted for Python API
   * @returns {Object} Formatted data ready for Python API
   */
  static async getDataForPythonAPI() {
    try {
      return await LocalStorageService.getDataForPythonAPI();
    } catch (error) {
      console.error('Error getting data for Python API:', error);
      throw error;
    }
  }

  /**
   * Clear all stored data and re-sync
   * Useful for troubleshooting or reset
   * @returns {Object} Sync result
   */
  static async resetAndSync() {
    try {
      console.log('Resetting and re-syncing...');
      await LocalStorageService.clearStoredData();
      return await this.sync({ forceSync: true });
    } catch (error) {
      console.error('Error resetting and syncing:', error);
      return {
        success: false,
        error: error.message,
        message: 'Failed to reset and sync',
      };
    }
  }
}

export default PlanSyncService;

